import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { Agent, Channel, EventFrame, Message, Part } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent, useChannel } from "../data/realtime";
import { applyEvent, applyUserMessage, initialChat, type ChatState } from "./model";

type Action = { type: "events"; events: Pick<EventFrame, "seq" | "type" | "data" | "at">[] } | { type: "messages"; messages: Message[] } | { type: "reset" };

function reducer(s: ChatState, a: Action): ChatState {
  switch (a.type) {
    case "events":
      return a.events.reduce(applyEvent, s);
    case "messages":
      return a.messages.reduce(applyUserMessage, s);
    case "reset":
      return initialChat();
  }
}

/**
 * 对话数据：先订阅实时流（缓冲），再用会话事件回放接口拉历史，历史应用完后衔接缓冲的实时事件（按 seq 去重）。
 * 用户消息来自 DM 会话（messageList + conversation:<cid> 实时）。
 */
export function useChat(agent: Agent) {
  const { api } = useServices();
  const [state, dispatch] = useReducer(reducer, undefined, initialChat);
  const [loading, setLoading] = useState(true);
  const [cid, setCid] = useState<string | null>(null);
  const loaded = useRef(false);
  const buffer = useRef<EventFrame[]>([]);
  const aid = agent.id;
  const sid = agent.currentSessionId;

  useChannel(`agent:${aid}:session` as Channel, {
    onEvent(e) {
      if (loaded.current) dispatch({ type: "events", events: [e] });
      else buffer.current.push(e);
    },
  });
  useChannel(cid ? (`conversation:${cid}` as Channel) : null, {
    onEvent(e) {
      if (isEvent(e, "message.created")) dispatch({ type: "messages", messages: [e.data] });
    },
  });

  useEffect(() => {
    let cancelled = false;
    loaded.current = false;
    setLoading(true);
    (async () => {
      const events: Pick<EventFrame, "seq" | "type" | "data" | "at">[] = [];
      if (sid) {
        let cursor: string | undefined;
        for (let i = 0; i < 50; i++) {
          const page = await api.call("agentSessionEvents", { params: { aid, sid }, query: { cursor, limit: 100 } });
          events.push(...(page.items as typeof events));
          if (!page.nextCursor) break;
          cursor = page.nextCursor;
        }
      }
      const convs = await api.call("conversationList", { params: { pid: agent.projectId }, query: { kind: "dm", limit: 100 } });
      const dm = convs.items.find((c) => c.members.some((m) => m.type === "agent" && m.id === aid));
      const messages = dm ? (await api.call("messageList", { params: { cid: dm.id }, query: { limit: 100 } })).items : [];
      if (cancelled) return;
      if (dm) setCid(dm.id);
      dispatch({ type: "reset" });
      dispatch({ type: "events", events });
      dispatch({ type: "messages", messages: [...messages].reverse() });
      dispatch({ type: "events", events: buffer.current.splice(0) });
      loaded.current = true;
      setLoading(false);
    })().catch(() => {
      if (cancelled) return;
      dispatch({ type: "events", events: buffer.current.splice(0) });
      loaded.current = true;
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // 只在切换 agent / 会话时重新拉历史
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid, sid]);

  const send = useCallback(
    async (parts: Part[]) => {
      const m = await api.call("agentInput", { params: { aid }, body: { parts } });
      dispatch({ type: "messages", messages: [m] });
      if (!cid) setCid(m.conversationId);
    },
    [api, aid, cid],
  );

  const interrupt = useCallback(async () => {
    await api.call("agentAction", { params: { aid, action: "interrupt" } });
  }, [api, aid]);

  return { state, loading, send, interrupt };
}
